import type { IMetadataKeyRepository } from '@app/domain/ports';
import { isSecondaryMetadataKey } from './metadata-key-routing-policy';

const STORAGE_PREFIX = 'fin:shadow:meta';
const ARRAY_BUFFER_TAG = 'fin_array_buffer';

type SerializedArrayBuffer = {
  __type: typeof ARRAY_BUFFER_TAG;
  data: number[];
};

const isSerializedArrayBuffer = (value: unknown): value is SerializedArrayBuffer => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<SerializedArrayBuffer>;
  return candidate.__type === ARRAY_BUFFER_TAG && Array.isArray(candidate.data);
};

const serializeMetadataValue = (value: unknown): string =>
  JSON.stringify(value, (_key, current) => {
    if (current instanceof ArrayBuffer) {
      return {
        __type: ARRAY_BUFFER_TAG,
        data: Array.from(new Uint8Array(current)),
      } satisfies SerializedArrayBuffer;
    }

    return current;
  });

const deserializeMetadataValue = (value: string): unknown =>
  JSON.parse(value, (_key, current) => {
    if (!isSerializedArrayBuffer(current)) return current;

    return new Uint8Array(current.data).buffer;
  });

export class LocalStorageMetadataKeyRepository implements IMetadataKeyRepository {
  async get<T = unknown>(key: string): Promise<T | undefined> {
    if (!isSecondaryMetadataKey(key)) return undefined;

    const raw = localStorage.getItem(this.prefixedKey(key));
    if (raw === null) return undefined;

    try {
      return deserializeMetadataValue(raw) as T;
    } catch (error) {
      console.warn('Failed to deserialize metadata value from localStorage', error);
      return undefined;
    }
  }

  async set<T = unknown>(key: string, value: T): Promise<void> {
    if (!isSecondaryMetadataKey(key)) return;

    localStorage.setItem(this.prefixedKey(key), serializeMetadataValue(value));
  }

  async delete(key: string): Promise<void> {
    if (!isSecondaryMetadataKey(key)) return;

    localStorage.removeItem(this.prefixedKey(key));
  }

  async clear(): Promise<void> {
    const keysToDelete: string[] = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && key.startsWith(`${STORAGE_PREFIX}:`)) {
        keysToDelete.push(key);
      }
    }

    for (const key of keysToDelete) {
      localStorage.removeItem(key);
    }
  }

  private prefixedKey(key: string): string {
    return `${STORAGE_PREFIX}:${key}`;
  }
}
